import type { Folder } from "@models";

import type { DocumentsPageItem } from "../Documents.utils";
import type { DocumentsTableMoveTarget } from "../DocumentsTable.types";

export type DocumentsTableFolderItemId = `folder:${string}`;
export type DocumentsTableDocumentItemId = `document:${string}`;
export type DocumentsTableItemId =
  | DocumentsTableFolderItemId
  | DocumentsTableDocumentItemId;

const ROW_DROP_TARGET_PREFIX = "documents-table-drop-target:item:";
const BREADCRUMB_ROOT_DROP_TARGET_ID =
  "documents-table-drop-target:breadcrumb:root";
const BREADCRUMB_FOLDER_DROP_TARGET_PREFIX =
  "documents-table-drop-target:breadcrumb:folder:";

export type DocumentsTableDropTargetId =
  `${typeof ROW_DROP_TARGET_PREFIX}${DocumentsTableItemId}`;

type DocumentItem = Extract<DocumentsPageItem, { kind: "document" }>;

export type DocumentsTableMovePlan = {
  documentMoves: Array<DocumentItem["document"]>;
  folderMoves: string[];
};

export const getDocumentsTableItemId = (
  item: DocumentsPageItem
): DocumentsTableItemId =>
  item.kind === "folder" ? `folder:${item.id}` : `document:${item.id}`;

export const getDocumentsTableFolderItemId = (
  folderId: string
): DocumentsTableFolderItemId => `folder:${folderId}`;

export const isDocumentsTableFolderItemId = (
  itemId: string
): itemId is DocumentsTableFolderItemId => itemId.startsWith("folder:");

export const isDocumentsTableDocumentItemId = (
  itemId: string
): itemId is DocumentsTableDocumentItemId => itemId.startsWith("document:");

export const isDocumentsTableItemId = (
  itemId: string
): itemId is DocumentsTableItemId =>
  isDocumentsTableFolderItemId(itemId) ||
  isDocumentsTableDocumentItemId(itemId);

export const getDocumentsTableFolderIdFromItemId = (
  itemId: DocumentsTableFolderItemId
) => itemId.slice("folder:".length);

export const getDocumentsTableRowDropTargetId = (
  itemId: DocumentsTableItemId
): DocumentsTableDropTargetId => `${ROW_DROP_TARGET_PREFIX}${itemId}`;

export const getDocumentsTableBreadcrumbRootDropTargetId = () =>
  BREADCRUMB_ROOT_DROP_TARGET_ID;

export const getDocumentsTableBreadcrumbFolderDropTargetId = (
  folderId: string
) => `${BREADCRUMB_FOLDER_DROP_TARGET_PREFIX}${folderId}`;

const getDocumentsTableItemIdFromRowDropTargetId = (
  dropTargetId: string
): DocumentsTableItemId | null => {
  const itemId = dropTargetId.startsWith(ROW_DROP_TARGET_PREFIX)
    ? dropTargetId.slice(ROW_DROP_TARGET_PREFIX.length)
    : "";

  return isDocumentsTableItemId(itemId) ? itemId : null;
};

const getDocumentsTableMoveTargetFromBreadcrumbDropTargetId = ({
  foldersById,
  targetId,
}: {
  foldersById: Map<string, Folder>;
  targetId: string;
}): DocumentsTableMoveTarget | null => {
  if (targetId === BREADCRUMB_ROOT_DROP_TARGET_ID) {
    return { type: "root" };
  }

  if (!targetId.startsWith(BREADCRUMB_FOLDER_DROP_TARGET_PREFIX)) {
    return null;
  }

  const folderId = targetId.slice(BREADCRUMB_FOLDER_DROP_TARGET_PREFIX.length);

  return foldersById.has(folderId) ? { type: "folder", folderId } : null;
};

export const buildDocumentsTableItemsById = (items: DocumentsPageItem[]) =>
  new Map(items.map((item) => [getDocumentsTableItemId(item), item]));

export const getDocumentsTableMoveSubjectIds = ({
  draggedItemId,
  selectedItemIds,
}: {
  draggedItemId: DocumentsTableItemId;
  selectedItemIds: DocumentsTableItemId[];
}) =>
  selectedItemIds.includes(draggedItemId) ? selectedItemIds : [draggedItemId];

const isFolderInsideFolder = ({
  ancestorFolderId,
  folderId,
  foldersById,
}: {
  ancestorFolderId: string;
  folderId: string;
  foldersById: Map<string, Folder>;
}) => {
  let currentFolderId: string | null = folderId;

  while (currentFolderId) {
    if (currentFolderId === ancestorFolderId) {
      return true;
    }

    currentFolderId = foldersById.get(currentFolderId)?.parentFolderId ?? null;
  }

  return false;
};

const isItemInsideSelectedFolder = ({
  folderId,
  foldersById,
  selectedFolderIds,
}: {
  folderId: string | null;
  foldersById: Map<string, Folder>;
  selectedFolderIds: string[];
}) =>
  Boolean(
    folderId &&
      selectedFolderIds.some((selectedFolderId) =>
        isFolderInsideFolder({
          ancestorFolderId: selectedFolderId,
          folderId,
          foldersById,
        })
      )
  );

export const resolveDocumentsTableMoveTarget = ({
  foldersById,
  itemsById,
  targetId,
}: {
  foldersById: Map<string, Folder>;
  itemsById: Map<DocumentsTableItemId, DocumentsPageItem>;
  targetId: string | null;
}): DocumentsTableMoveTarget | null => {
  if (!targetId) {
    return null;
  }

  const breadcrumbTarget =
    getDocumentsTableMoveTargetFromBreadcrumbDropTargetId({
      foldersById,
      targetId,
    });

  if (breadcrumbTarget) {
    return breadcrumbTarget;
  }

  const targetItemId = getDocumentsTableItemIdFromRowDropTargetId(targetId);
  const targetItem = targetItemId ? itemsById.get(targetItemId) : null;

  if (!targetItem) {
    return null;
  }

  if (targetItem.kind === "folder") {
    return { type: "folder", folderId: targetItem.id };
  }

  return targetItem.folderId && foldersById.has(targetItem.folderId)
    ? { type: "folder", folderId: targetItem.folderId }
    : { type: "root" };
};

export const buildDocumentsTableMovePlan = ({
  foldersById,
  itemIds,
  itemsById,
  target,
}: {
  foldersById: Map<string, Folder>;
  itemIds: DocumentsTableItemId[];
  itemsById: Map<DocumentsTableItemId, DocumentsPageItem>;
  target: DocumentsTableMoveTarget;
}): DocumentsTableMovePlan | null => {
  const nextFolderId = target.type === "folder" ? target.folderId : null;
  const selectedFolderIds = itemIds
    .filter(isDocumentsTableFolderItemId)
    .map(getDocumentsTableFolderIdFromItemId);
  const topLevelSelectedFolderIds = selectedFolderIds.filter(
    (folderId) =>
      !selectedFolderIds.some(
        (selectedFolderId) =>
          selectedFolderId !== folderId &&
          isFolderInsideFolder({
            ancestorFolderId: selectedFolderId,
            folderId,
            foldersById,
          })
      )
  );

  if (
    nextFolderId &&
    topLevelSelectedFolderIds.some((folderId) =>
      isFolderInsideFolder({
        ancestorFolderId: folderId,
        folderId: nextFolderId,
        foldersById,
      })
    )
  ) {
    return null;
  }

  const folderMoves = topLevelSelectedFolderIds.filter((folderId) => {
    const folder = foldersById.get(folderId);
    return folder && (folder.parentFolderId ?? null) !== nextFolderId;
  });
  const documentMoves = itemIds.flatMap((itemId) => {
    if (!isDocumentsTableDocumentItemId(itemId)) {
      return [];
    }

    const item = itemsById.get(itemId);

    if (
      item?.kind !== "document" ||
      (item.folderId ?? null) === nextFolderId ||
      isItemInsideSelectedFolder({
        folderId: item.folderId,
        foldersById,
        selectedFolderIds: topLevelSelectedFolderIds,
      })
    ) {
      return [];
    }

    return [item.document];
  });

  return folderMoves.length || documentMoves.length
    ? { documentMoves, folderMoves }
    : null;
};
